import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary — catches any uncaught render error in a subtree and shows
 * a friendly fallback instead of a blank white page. Used to wrap pages
 * that pull dynamic data (e.g. BookingDetail) so a single bad field on
 * one record can't kill the whole app.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="error-boundary-fallback">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 mb-4">
          <AlertTriangle className="h-7 w-7 text-amber-700" />
        </div>
        <h2 className="font-display text-2xl text-stone-900 mb-2">Something went wrong on this page</h2>
        <p className="text-stone-600 mb-6">
          We hit an unexpected issue rendering this view. The rest of COMPANIN is still working — try reloading, or head back to your bookings.
        </p>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white px-5 py-2 text-sm font-medium"
            data-testid="error-boundary-reload"
          >
            <RefreshCw className="h-4 w-4" /> Reload page
          </button>
          <a
            href="/bookings"
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 text-stone-800 hover:bg-stone-50 px-5 py-2 text-sm font-medium"
          >
            Back to bookings
          </a>
        </div>
      </div>
    );
  }
}
